import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, User, setPersistence, browserLocalPersistence, updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Check if Firebase is enabled
export const firebaseEnabled = !!import.meta.env.VITE_FIREBASE_API_KEY && !!import.meta.env.VITE_FIREBASE_PROJECT_ID;

// Initialize Firebase only if enabled
let app: any = null;
let auth: any = null;
let db: any = null;

if (firebaseEnabled) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);

  // Configure authentication persistence to remember login across sessions
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log('Firebase Auth persistence enabled');
    })
    .catch((error) => {
      console.warn('Failed to enable Firebase Auth persistence:', error);
    });

  console.log('Firebase initialized successfully');

  // Handle redirect result (for popup-blocked browsers or Safari)
  try {
    getRedirectResult(auth)
      .then((result) => {
        if (result && result.user) {
          console.log('Google sign-in via redirect successful:', result.user.uid);
        }
      })
      .catch((err) => {
        console.warn('Google redirect sign-in result check failed:', err);
      });
  } catch {}
} else {
  console.warn('Firebase disabled - running in offline mode (cloud features disabled).');
}

export { auth, db };

// Test Firebase connection
export const testFirebaseConnection = async (): Promise<{ success: boolean; message: string }> => {
  if (!firebaseEnabled || !db) {
    return { success: false, message: 'Firebase not enabled' };
  }

  try {
    const testRef = doc(db, 'test', 'connection');
    await getDoc(testRef);
    return { success: true, message: 'Firebase connection successful' };
  } catch (error) {
    console.error('Firebase connection test failed:', error);
    return {
      success: false,
      message: `Firebase connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
};

// Auth providers
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Decide if popup is unreliable and redirect should be preferred (e.g., Safari/iOS)
const shouldUseRedirect = (): boolean => {
  try {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|Edg|OPR/.test(ua);
    return isIOS || isSafari;
  } catch {
    return false;
  }
};

// Auth functions
export const signInWithGoogle = async (): Promise<User | null> => {
  if (!firebaseEnabled || !auth) {
    console.warn('Firebase not available - cannot sign in');
    return null;
  }

  try {
    if (shouldUseRedirect()) {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    const code = (error as any)?.code || '';
    console.error('Error signing in with Google:', error);
    if (
      code === 'auth/popup-blocked' ||
      code === 'auth/cancelled-popup-request' ||
      code === 'auth/operation-not-supported-in-this-environment'
    ) {
      try {
        await signInWithRedirect(auth, googleProvider);
        return null;
      } catch (redirectErr) {
        console.error('Redirect sign-in failed:', redirectErr);
        throw redirectErr;
      }
    }
    throw error;
  }
};

export const signOutUser = async (): Promise<void> => {
  if (!firebaseEnabled || !auth) {
    console.warn('Firebase not available - cannot sign out');
    return;
  }

  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

export const updateUserProfile = async (displayName?: string, photoURL?: string): Promise<void> => {
  if (!firebaseEnabled || !auth) {
    throw new Error('Firebase not available - cannot update profile');
  }

  if (!auth.currentUser) {
    throw new Error('No authenticated user - please sign in first');
  }

  try {
    const updates: { displayName?: string; photoURL?: string } = {};
    if (displayName !== undefined) updates.displayName = displayName;
    if (photoURL !== undefined) updates.photoURL = photoURL;

    await updateProfile(auth.currentUser, updates);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const changeUserPassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  if (!firebaseEnabled || !auth) {
    throw new Error('Firebase not available - cannot change password');
  }

  if (!auth.currentUser) {
    throw new Error('No authenticated user - please sign in first');
  }

  if (!auth.currentUser.email) {
    throw new Error('Email/password authentication required for password change');
  }

  try {
    const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
    await updatePassword(auth.currentUser, newPassword);
  } catch (error: any) {
    console.error('Error changing password:', error);
    if (error.code === 'auth/wrong-password') {
      throw new Error('Current password is incorrect');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('New password is too weak. Please choose a stronger password.');
    } else if (error.code === 'auth/requires-recent-login') {
      throw new Error('Please sign out and sign back in before changing your password');
    } else {
      throw new Error('Failed to change password. Please try again.');
    }
  }
};

export const onAuthStateChange = (callback: (user: User | null) => void) => {
  if (!firebaseEnabled || !auth) {
    console.warn('Firebase not available - auth state monitoring disabled');
    return () => {};
  }

  return onAuthStateChanged(auth, callback);
};

// Utility functions
export const getCurrentUser = (): User | null => {
  if (!firebaseEnabled || !auth) {
    return null;
  }
  return auth.currentUser;
};

export const isUserSignedIn = (): boolean => {
  if (!firebaseEnabled || !auth) {
    return false;
  }
  return !!auth.currentUser;
};
